// src/api/auth.js
/**
 * Authentication Utilities
 * 
 * Centralized authentication helpers for API requests.
 * Handles JWT token retrieval from AWS Amplify and header construction.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Helper function to get the current user's JWT ID token (identity — who the user is).
 * @returns {Promise<string>} The ID token string for identity-related calls
 * @throws {Error} If authentication fails or no token is found
 */
export const getIdToken = async () => {
    try {
        console.log('AUTH: Fetching authentication session...');
        const session = await fetchAuthSession();

        if (!session?.tokens?.idToken) {
            console.error('AUTH: No ID token found in session');
            throw new Error('User is not authenticated');
        }

        console.log('AUTH: Successfully retrieved ID token');
        return session.tokens.idToken.toString();
    } catch (error) {
        console.error('AUTH: Failed to get ID token:', error);
        throw new Error('Authentication failed. Please sign in again.');
    }
};

/**
 * Helper function to get the current user's JWT Access token (authorization — what the user can do).
 * This is the correct token type for API Gateway / WebSocket authorization.
 * Falls back to the ID token if no access token is present (should not happen in normal flows).
 * @returns {Promise<string>} The access token string for API authorization
 * @throws {Error} If authentication fails or no token is found
 */
export const getAccessToken = async () => {
    try {
        const session = await fetchAuthSession();

        if (session?.tokens?.accessToken) {
            console.log('AUTH: Successfully retrieved access token');
            return session.tokens.accessToken.toString();
        }

        // Fallback: access token unavailable, use ID token
        if (session?.tokens?.idToken) {
            console.warn('AUTH: Access token unavailable, falling back to ID token');
            return session.tokens.idToken.toString();
        }

        throw new Error('User is not authenticated');
    } catch (error) {
        console.error('AUTH: Failed to get access token:', error);
        throw new Error('Authentication failed. Please sign in again.');
    }
};

/**
 * Helper function to create authenticated headers for JSON requests
 * @returns {Promise<object>} Headers object with Authorization and Content-Type
 */
export const getAuthHeaders = async () => {
    const token = await getIdToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

/**
 * Helper function to create authenticated headers without Content-Type
 * Useful for requests where Content-Type is set automatically (e.g., FormData)
 * @returns {Promise<object>} Headers object with Authorization only
 */
export const getAuthHeadersOnly = async () => {
    const token = await getIdToken();
    return {
        'Authorization': `Bearer ${token}`
    };
};

/**
 * Handles authentication-related errors consistently
 * @param {number} status - HTTP status code
 * @param {string} context - Context description for logging
 * @throws {Error} Appropriate error based on status code
 */
export const handleAuthError = (status, context = 'request') => {
    if (status === 401) {
        console.error(`AUTH: Authentication failed during ${context} - 401 response`);
        throw new Error('Authentication failed. Please sign in again.');
    }
    
    if (status === 403) {
        console.error(`AUTH: Access denied during ${context} - 403 response`);
        throw new Error('Access denied. You do not have permission for this action.');
    }
};

/**
 * Validates that a user session exists and is valid
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 */
export const isAuthenticated = async () => {
    try {
        await getIdToken();
        return true;
    } catch {
        return false;
    }
};